import { Plugin, Widget } from 'ckeditor5'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { toWidget } from 'ckeditor5'
import Riddle from '@minicoursegenerator/riddle'

// Transform config - API data is already in correct format, just pass through
const transformConfigForRiddle = (config) => {
    if (!config) return config
    // API format is already correct: { questTitle, language, colors, riddles }
    return { ...config }
}

const DEFAULT_RIDDLE_JSON = {
    questTitle: 'Mystery Quest',
    language: 'en',
    colors: {
        primary: '#4f46e5',
        background: '#ffffff',
        cardBackground: '#ffffff',
        textMain: '#0f172a',
        textSecondary: '#64748b',
        accent: '#6366f1',
        success: '#059669'
    },
    riddles: [
        {
            answer: 'Piano',
            hints: [
                'I have keys but no locks',
                'I have space but no room',
                'You can enter but cannot go inside',
                'What am I?'
            ]
        }
    ]
}

export default class RiddlePlugin extends Plugin {
    static get pluginName() {
        return 'RiddlePlugin'
    }

    static get requires() {
        return [Widget]
    }

    constructor(editor) {
        super(editor)
        this.mountedRoots = new Map()
        this._updateTimeout = null
        this._lastConfigStr = null
    }

    init() {
        const editor = this.editor

        editor.model.schema.register('riddle', {
            isObject: true,
            allowWhere: '$block',
            allowIn: 'column',
            allowAttributes: [
                'id',
                'data',
                'gameConfig',
            ],
        })

        this._defineConverters()
        this._setupAttributeListener()

        editor.ui.componentFactory.add('insertRiddle', locale => {
            const button = document.createElement('button')
            button.className = 'ck-button ck-button_with-text'
            button.style.display = 'flex'
            button.style.alignItems = 'center'
            button.style.gap = '4px'
            button.style.padding = '8px 12px'

            const icon = document.createElement('span')
            icon.textContent = '🧩'
            icon.style.fontSize = '16px'

            const text = document.createTextNode('Riddle')

            button.appendChild(icon)
            button.appendChild(text)

            button.onclick = () => {
                editor.execute('insertRiddle')
            }

            return {
                element: button,
                render: () => { },
                destroy: () => { },
            }
        })

        // Command to insert via button (uses default JSON)
        editor.commands.add('insertRiddle', {
            execute: () => {
                editor.model.change(writer => {
                    const riddleLanguage = editor.riddleLanguage || 'en'
                    const newConfig = JSON.parse(JSON.stringify(DEFAULT_RIDDLE_JSON))
                    if (newConfig.config) {
                        newConfig.config.language = riddleLanguage
                    }

                    // Get bgColor from config or colors.background
                    const bgColor = newConfig?.config?.bgColor || newConfig?.colors?.background || '#ffffff'

                    const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
                        backgroundColor: bgColor,
                    })
                    const simpleBox = writer.createElement('simpleBox')
                    const column = writer.createElement('column')
                    const riddle = writer.createElement('riddle', {
                        gameConfig: JSON.stringify(newConfig),
                    })

                    writer.append(simpleBox, simpleBoxWrapper)
                    writer.append(column, simpleBox)
                    writer.append(riddle, column)

                    editor.model.insertContent(simpleBoxWrapper)
                    writer.setSelection(riddle, 'on')
                })
            },
        })
    }

    _setupAttributeListener() {
        const editor = this.editor

        editor.model.document.on('change:data', () => {
            const changes = editor.model.document.differ.getChanges()

            for (const change of changes) {
                if (change.type === 'attribute' && change.attributeKey === 'gameConfig') {
                    const modelElement = change.range.start.nodeAfter
                    if (modelElement && modelElement.name === 'riddle') {
                        const newGameConfig = modelElement.getAttribute('gameConfig')
                        this._updateRiddleComponent(modelElement, newGameConfig)
                    }
                }
            }
        })
    }

    _updateRiddleComponent(modelElement, gameConfig) {
        if (gameConfig === this._lastConfigStr) {
            return
        }

        this._lastConfigStr = gameConfig

        if (this._updateTimeout) {
            clearTimeout(this._updateTimeout)
        }

        this._updateTimeout = setTimeout(() => {
            const editor = this.editor

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (!viewElement) return

            const domConverter = editor.editing.view.domConverter
            const containerDom = domConverter.mapViewToDom(viewElement)
            if (!containerDom) return

            const placeholder = containerDom.querySelector('.riddle-react-placeholder')
            if (!placeholder) return

            const existingRoot = this.mountedRoots.get(placeholder)
            if (existingRoot) {
                try {
                    const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_RIDDLE_JSON
                    const transformedConfig = transformConfigForRiddle(parsedConfig)
                    // Get language from config (priority: config.language > language > editor default)
                    const configLanguage = transformedConfig?.config?.language || transformedConfig?.language || editor.riddleLanguage || 'en'

                    const props = {
                        data: transformedConfig,
                        language: configLanguage,
                        onComplete: () => console.log('🧩 Solved!'),
                        onFail: () => console.log('🧩 Failed!'),
                        onChange: state => console.log('🧩 State:', state),
                    }

                    console.log('🧩 Riddle re-rendering with new config:', props)
                    existingRoot.render(React.createElement(Riddle, props))
                } catch (error) {
                    console.error('Error re-rendering Riddle component:', error)
                }
            }
        }, 500)
    }

    _defineConverters() {
        const editor = this.editor
        const conversion = editor.conversion

        conversion.for('upcast').elementToElement({
            view: {
                name: 'div',
                attributes: {
                    'data-type': 'riddle',
                },
            },
            model: (viewElement, { writer }) => {
                const id = viewElement.getAttribute('id')
                const data = viewElement.getAttribute('data') || ''
                const gameConfig = viewElement.getAttribute('data-game-config') || ''

                const attrs = {
                    data,
                    gameConfig,
                }
                if (id) attrs.id = id

                return writer.createElement('riddle', attrs)
            },
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'riddle',
            view: (modelElement, { writer }) => {
                const id = modelElement.getAttribute('id')
                const data = modelElement.getAttribute('data') || ''
                const gameConfig = modelElement.getAttribute('gameConfig') || ''

                const attrs = {
                    'data-type': 'riddle',
                    data: data,
                    'data-game-config': gameConfig,
                }

                if (id !== undefined && id !== null) {
                    attrs.id = id
                }

                return writer.createEmptyElement('div', attrs)
            },
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'riddle',
            view: (modelElement, { writer }) => {
                const gameConfig = modelElement.getAttribute('gameConfig') || ''

                const containerAttrs = {
                    class: 'riddle-widget-container',
                    'data-type': 'riddle',
                    'data-game-config': gameConfig,
                }

                const container = writer.createContainerElement('div', containerAttrs)

                const placeholder = writer.createRawElement(
                    'div',
                    {
                        class: 'riddle-react-placeholder',
                        // intentionally no contenteditable here
                    },
                    domElement => {
                        // Stop propagation of events
                        const stopPropagation = e => e.stopPropagation()
                        const events = ['mousedown', 'click', 'dblclick', 'pointerdown', 'pointermove', 'pointerup', 'keydown', 'keyup', 'input']
                        events.forEach(evt => domElement.addEventListener(evt, stopPropagation))
                        domElement.addEventListener('focus', stopPropagation, true)
                        domElement.addEventListener('blur', stopPropagation, true)

                        console.log('🧩 Riddle rendering started')
                        try {
                            const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_RIDDLE_JSON
                            const transformedConfig = transformConfigForRiddle(parsedConfig)
                            // Get language from config (priority: config.language > language > editor default)
                            const configLanguage = transformedConfig?.config?.language || transformedConfig?.language || editor.riddleLanguage || 'en'

                            const props = {
                                data: transformedConfig,
                                language: configLanguage,
                                onComplete: () => console.log('🧩 Solved!'),
                                onFail: () => console.log('🧩 Failed!'),
                                onChange: state => console.log('🧩 State:', state),
                            }

                            console.log('🧩 Riddle Props (Initial):', props)

                            const root = createRoot(domElement)
                            root.render(React.createElement(Riddle, props))

                            editor.plugins
                                .get('RiddlePlugin')
                                .mountedRoots.set(domElement, root)
                        } catch (error) {
                            console.error('Error rendering Riddle component:', error)
                            domElement.innerHTML = `
                <div style="padding: 20px; background: #f5f5f5; border-radius: 8px; text-align: center;">
                  <span style="font-size: 24px;">🧩</span>
                  <p>Riddle</p>
                  <small>Component not available</small>
                </div>
              `
                        }
                    },
                )

                writer.insert(writer.createPositionAt(container, 0), placeholder)

                return toWidget(container, writer, {
                    label: 'Riddle',
                    hasSelectionHandle: true,
                })
            },
        })
    }
}
